import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setBuilderTextStyle } from "../../lib/metadata-builder/edit-text";
import { loadArtFont } from "../../lib/schedule-art/art-fonts";
import { useCanvasMetadata } from "../../lib/schedule-art/canvas-state";
import { baseImagePalette } from "../../lib/metadata-builder/image-palette";
import { builderSessionId } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { NativeSelect } from "../ui/native-select";
import { TextFontPicker } from "./TextFontPicker";
import { TextNumberField } from "./TextNumberField";
import { TextColorPicker } from "./TextColorPicker";
import { TextColorSwatches } from "./TextColorSwatches";
import type { ArtTextStyle } from "../../lib/schedule-presets/art-metadata";

type StyleChange = (patch: Partial<ArtTextStyle>, field: keyof ArtTextStyle, merge?: boolean) => void;

type TextStyleControlsProps = { styleName: string; onChange?: StyleChange };

export function TextStyleControls({ styleName, onChange }: TextStyleControlsProps) {
  const { t } = useTranslation();
  const metadata = useCanvasMetadata();
  const style = metadata.styles[styleName];
  const base = useMetadataHistoryStore(state => state.sessions[builderSessionId]?.artwork?.base);
  const palette = useMemo(() => baseImagePalette(base), [base]);
  const [fontState, setFontState] = useState<"loading" | "ready" | "failed">("ready");
  useEffect(() => {
    if (!style) return;
    let disposed = false;
    const checkFont = () => {
      if (style.font === "sans-serif") { setFontState("ready"); return; }
      setFontState("loading");
      loadArtFont(style).then(loaded => { if (!disposed) setFontState(loaded ? "ready" : "failed"); });
    };
    checkFont();
    document.fonts.addEventListener("loadingdone", checkFont);
    return () => { disposed = true; document.fonts.removeEventListener("loadingdone", checkFont); };
  }, [style?.font, style?.weight]);
  if (!style) return null;
  const changeStyle: StyleChange = onChange ?? ((patch, field, merge) => setBuilderTextStyle(styleName, patch, field, merge));
  const stroke = style.stroke ?? { color: "#000000", width: 2, position: "outside" as const };
  return (
    <div className="@container min-w-0 space-y-3">
      <TextFontPicker font={style.font} onChange={(font, weights) => {
        const weight = weights?.reduce((nearest, candidate) => Math.abs(candidate - style.weight) < Math.abs(nearest - style.weight) ? candidate : nearest);
        changeStyle({ font, weight: weight ?? style.weight }, "font");
      }} />
      {fontState === "loading" && <p className="text-neutral-500">{t("builder.font.loading")}</p>}
      {fontState === "failed" && <p className="text-red-600">{t("builder.font.failed")}</p>}
      <div className="grid grid-cols-2 items-start gap-2 @sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)]">
        <TextNumberField label={t("metadata.field.size")} value={style.size} min={1} onChange={size => changeStyle({ size }, "size", true)} />
        <label className="grid min-w-0 gap-1 text-xs font-medium">
          {t("metadata.field.weight")}
          <NativeSelect value={style.weight} className="h-8 py-1 text-xs" onChange={event => changeStyle({ weight: Number(event.target.value) }, "weight")}>
            {[100, 200, 300, 400, 500, 600, 700, 800, 900].map(weight => <option key={weight} value={weight}>{weight}</option>)}
          </NativeSelect>
        </label>
        <div className="col-span-2 min-w-0 @sm:col-span-1">
          <TextColorPicker label={t("metadata.field.color")} value={style.color} palette={palette}
            onChange={color => changeStyle({ color }, "color", true)} />
        </div>
      </div>
      <TextColorSwatches palette={palette} value={style.color} onChange={color => changeStyle({ color }, "color")} />
      <div className="grid min-w-0 items-end gap-2 border-t border-neutral-200 pt-3 @sm:grid-cols-2">
        <label className="grid min-w-0 gap-1 text-xs font-medium">
          {t("metadata.field.align")}
          <NativeSelect value={style.align ?? "left"} className="h-8 py-1 text-xs" onChange={event => {
            const align = event.target.value;
            if (align === "left" || align === "center" || align === "right") changeStyle({ align }, "align");
          }}>
            <option value="left">{t("builder.style.left")}</option>
            <option value="center">{t("builder.style.center")}</option>
            <option value="right">{t("builder.style.right")}</option>
          </NativeSelect>
        </label>
        <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={style.capitalize ?? false} className="accent-neutral-900" onChange={event => changeStyle({ capitalize: event.target.checked }, "capitalize")} />
            {t("builder.style.capitalize")}
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={!!style.stroke} className="accent-neutral-900" onChange={event => changeStyle({ stroke: event.target.checked ? stroke : undefined }, "stroke")} />
            {t("builder.style.stroke")}
          </label>
        </div>
        {style.stroke && <div className="grid grid-cols-2 items-start gap-2 @sm:col-span-2 @sm:grid-cols-3">
          <TextNumberField label={t("metadata.field.width")} value={stroke.width} min={0} step={0.5} onChange={width => changeStyle({ stroke: { ...stroke, width } }, "stroke", true)} />
          <TextColorPicker label={t("metadata.field.color")} value={stroke.color} palette={palette}
            onChange={color => changeStyle({ stroke: { ...stroke, color } }, "stroke", true)} />
          <label className="col-span-2 grid min-w-0 gap-1 text-xs font-medium @sm:col-span-1">
            {t("builder.style.strokePosition")}
            <NativeSelect value={stroke.position ?? "center"} className="h-8 py-1 text-xs" onChange={event => {
              const position = event.target.value;
              if (position === "center" || position === "outside") changeStyle({ stroke: { ...stroke, position } }, "stroke");
            }}>
              <option value="center">{t("metadata.stroke.center")}</option>
              <option value="outside">{t("metadata.stroke.outside")}</option>
            </NativeSelect>
          </label>
          <div className="col-span-2 @sm:col-span-3">
            <TextColorSwatches palette={palette} value={stroke.color}
              onChange={color => changeStyle({ stroke: { ...stroke, color } }, "stroke")} />
          </div>
        </div>}
      </div>
    </div>
  );
}
